import { descendantsOf } from "../program-access/access-model";
import { pathText } from "../program-timeline/move-destinations";
import { flattenProgramTree } from "../program-tree/program-tree";

import type { ProgramNode, ProgramTree } from "../program-tree/program-tree";

export interface ProgramMoveTarget {
  node: ProgramNode;
  depth: number;
  /** The titles of the programs above it, from the top down. */
  path: string;
  /** Why it cannot be picked: it is the program, inside it, or where it is now. */
  tag: string | null;
  disabled: boolean;
}

/**
 * The programs and folders that a program can move into, in the order the switcher shows them, with
 * a query on the title. The program itself, each folder inside it, and its parent show, but cannot
 * be picked.
 */
export const programMoveTargets = (
  tree: ProgramTree,
  moving: ProgramNode,
  query: string,
): ProgramMoveTarget[] => {
  const kind = moving.parent_id ? "folder" : "program";
  const inside = new Set(descendantsOf(tree, moving.id).map(({ node }) => node.id));
  const search = query.trim().toLowerCase();
  return flattenProgramTree(tree)
    .filter(({ node }) => !search || node.title.toLowerCase().includes(search))
    .map(({ node, depth }) => {
      let tag: string | null = null;
      if (node.id === moving.id) tag = `This ${kind}`;
      else if (inside.has(node.id)) tag = `Inside this ${kind}`;
      else if (node.id === moving.parent_id) tag = "Current location";
      return {
        node,
        depth,
        path: node.parent_id ? pathText(tree, node.parent_id) : "",
        tag,
        disabled: tag !== null,
      };
    });
};

const sameTitle = (a: string, b: string): boolean =>
  a.trim().toLowerCase() === b.trim().toLowerCase();

/**
 * Another program in the new place, a parent or the top level, has the title already. A hidden
 * program counts too, as move_program refuses its title.
 */
export const titleTaken = (
  tree: ProgramTree,
  moving: ProgramNode,
  parentId: string | null,
  title: string,
): boolean =>
  [...tree.byId.values()].some(
    (node) => node.parent_id === parentId && node.id !== moving.id && sameTitle(node.title, title),
  );

/** The program's title, or "Title (2)" with the first number that is free in the new place. */
export const freeTitle = (
  tree: ProgramTree,
  moving: ProgramNode,
  parentId: string | null,
): string => {
  let { title } = moving;
  for (let n = 2; titleTaken(tree, moving, parentId, title); n += 1)
    title = `${moving.title} (${n})`;
  return title;
};
